import React, { useMemo } from 'react';
import {
  ITagOptionInput,
  TagOption,
} from '../../../utils/apollo/graphqlSchema';
import { usePanelStateContext } from './context';
import Panel from './panel/Panel';
import usePanelStack from './usePanelStack';
import produce from 'immer';
import { CREATABLE_NODE, SEPARATOR_NODE } from './constants';
import { ISearchTagsState } from './types';
import EmptyPanelStack from './EmptyPanelStack';

const shouldShowEventDataRefresh = (tags: ITagOptionInput[]) =>
  tags.some((t) => t.tag === TagOption.BasicEvent);

interface IProps {
  searchTagsState: ISearchTagsState;
}

const PanelStack = ({ searchTagsState }: IProps) => {
  const { tags, isCreatable, selectedEntities } = usePanelStateContext();

  // inject creatable option if supported
  const creatableSearchTagsState = useMemo(() => {
    if (isCreatable) {
      return produce(searchTagsState, (draft) => {
        const rootChildren = draft.root.children;
        rootChildren.unshift(CREATABLE_NODE);
        // adding a separator between creatable node and the rest only when there are more items after it
        // (rootChildren.length is at least 1 - the creatable node itself we just added)
        if (rootChildren.length > 1) {
          rootChildren.splice(1, 0, SEPARATOR_NODE);
        }
      });
    } else {
      return searchTagsState;
    }
  }, [searchTagsState, isCreatable]);

  // build the horizontal stack of panels from the search result
  const { panelStack } = usePanelStack(
    creatableSearchTagsState,
    selectedEntities,
    shouldShowEventDataRefresh(tags),
  );

  return (
    <>
      {panelStack.length === 0 ? (
        <EmptyPanelStack tags={tags.map((t) => t.tag)} />
      ) : (
        panelStack.map((panel, index) => (
          // using index for a key is actually correct because slots are sequential
          <Panel panel={panel} key={`panel-${panel.type}-${index}`} />
        ))
      )}
    </>
  );
};
export default PanelStack;
